import { useState, useEffect, useCallback } from 'react';
import { themesApi } from '../api';
import type { ThemeWithOwnership, ThemeCategory } from '../types';

/**
 * Hook for fetching all available themes with ownership status
 */
export function useThemes() {
  const [themes, setThemes] = useState<ThemeWithOwnership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThemes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await themesApi.listThemes();
      setThemes(data.themes || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch themes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  return {
    themes,
    isLoading,
    error,
    refresh: fetchThemes,
  };
}

/**
 * Hook for fetching themes by category
 */
export function useThemesByCategory(category: ThemeCategory | null) {
  const [themes, setThemes] = useState<ThemeWithOwnership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThemes = useCallback(async () => {
    if (!category) {
      setThemes([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await themesApi.listByCategory(category);
      setThemes(data.themes || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch themes');
    } finally {
      setIsLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  return {
    themes,
    isLoading,
    error,
    refresh: fetchThemes,
  };
}

/**
 * Hook for fetching a single theme by ID or lookup key
 */
export function useTheme(idOrKey: string | null) {
  const [theme, setTheme] = useState<ThemeWithOwnership | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTheme = useCallback(async () => {
    if (!idOrKey) {
      setTheme(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await themesApi.getTheme(idOrKey);
      setTheme(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch theme');
    } finally {
      setIsLoading(false);
    }
  }, [idOrKey]);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  return {
    theme,
    isLoading,
    error,
    refresh: fetchTheme,
  };
}

/**
 * Hook for fetching user's owned themes
 */
export function useOwnedThemes() {
  const [themes, setThemes] = useState<ThemeWithOwnership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThemes = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await themesApi.getOwnedThemes();
      setThemes(data.themes || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch owned themes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  return {
    themes,
    isLoading,
    error,
    refresh: fetchThemes,
  };
}

/**
 * Hook for applying/removing themes from boards
 */
export function useBoardTheme(boardId: string) {
  const [isApplying, setIsApplying] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyTheme = useCallback(async (themeId: string) => {
    if (!boardId) return;
    setIsApplying(true);
    setError(null);
    try {
      await themesApi.applyThemeToBoard(boardId, themeId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply theme';
      setError(message);
      throw err;
    } finally {
      setIsApplying(false);
    }
  }, [boardId]);

  const removeTheme = useCallback(async () => {
    if (!boardId) return;
    setIsRemoving(true);
    setError(null);
    try {
      await themesApi.removeThemeFromBoard(boardId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove theme';
      setError(message);
      throw err;
    } finally {
      setIsRemoving(false);
    }
  }, [boardId]);

  return {
    applyTheme,
    removeTheme,
    isApplying,
    isRemoving,
    error,
  };
}
